import React, { useState, useMemo, useEffect } from "react";
import ActionBar from "../../common/ActionBar";
import _ from "lodash";
import { navigate } from "@reach/router";
import { Autocomplete } from "@material-ui/lab";
import { CustomInputField } from "../../common/FormInputs";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { Chip, CircularProgress, makeStyles, Tooltip } from "@material-ui/core";
import PartyService from "../../../services/PartyService";
import { useSnackbar } from "notistack";
import CustomTable from "./MaterialTable";
import { Cancel } from "@material-ui/icons";
import debounce from "lodash/debounce";
import CaseReports from "./CaseReports";
import {
  Container as DetailContainer,
  CommentCard,
  CommentContent,
  Label,
  Value,
} from "../FinalApprover/component/CommentSplitUp";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: "25px",
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
  chipLabel: {
    fontSize: "14px",
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
  },
  deleteIcon: {
    color: "#d91b1b",
    "&:hover": {
      color: "#d91b1b",
    },
  },
  customTooltip: {
    color: "#ffffff",
    fontSize: "12px",
    borderRadius: "8px",
    fontFamily: "openSans-SemiBold, sans-serif",
  },
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    "& li[data-option-index]": {
      padding: "12px 16px !important",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#f0f9ff",
        paddingLeft: "20px !important",
      },
    },
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
    padding: "16px",
    fontSize: "14px",
  },
  paper: {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
    borderRadius: "8px !important",
    border: "1px solid #e2e8f0 !important",
  },
}));

const PAGE_SIZE = 50;

const BulkUploadReports = () => {
  const classes = useStyles();
  const [selectedParties, setSelectedParties] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [currentData, setCurrentData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [state, setState] = useState({ state: false, message: "" });

  // Debounced server search
  const debouncedFetch = useMemo(
    () =>
      debounce(async (searchTerm) => {
        if (searchTerm.length < 2) {
          setOptions([]);
          return;
        }

        try {
          setLoading(true);
          const queryParams = `?search=${encodeURIComponent(
            searchTerm,
          )}&page=1&perPage=${PAGE_SIZE}&status=active`;
          const response = await PartyService.index(queryParams);

          if (response?.data?.length) {
            const newOptions = response.data.map(({ name, id, owner }) => ({
              label: name,
              value: id,
              email: owner?.email || "No email",
              id,
            }));
            setOptions(newOptions);
          } else {
            setOptions([]);
          }
        } catch (error) {
          enqueueSnackbar("Failed to search parties", { variant: "error" });
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 400),
    [enqueueSnackbar],
  );

  // Trigger search on input change
  useEffect(() => {
    if (open) {
      debouncedFetch(inputValue);
    }
  }, [inputValue, open, debouncedFetch]);

  // Fetch reports when selected parties change
  useEffect(() => {
    async function getDocumentsReport() {
      try {
        setTableLoading(true);
        const stringParams = selectedParties.length
          ? `?partyIds=${selectedParties.map((p) => p.id).join(",")}`
          : "?partyIds=";

        const response = await PartyService.getDocumentsReport(stringParams);
        setCurrentData(response || []);
      } catch (err) {
        enqueueSnackbar(err?.message || "Failed to load reports", {
          variant: "error",
        });
        setCurrentData([]);
      } finally {
        setTableLoading(false);
      }
    }

    if (selectedParties.length) {
      getDocumentsReport();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParties]);

  const onBreadcrumbClick = (selected) => {
    if (_.snakeCase(selected) === "dashboard") {
      navigate("/dashboard/case-manager");
    }
  };

  const actions = [
    {
      text: "Reports",
      props: {
        onClick: () => {
          setState({ state: true, message: "Downloading reports..." });
        },
      },
    },
  ];

  const columns = [
    {
      field: "caseId",
      title: "Case ID",
      render: (rowData) => <Center>{rowData?.caseId}</Center>,
      width: "12%",
    },
    {
      field: "title",
      title: "Title",
      sorting: true,
      render: (rowData) => (
        <Tooltip
          title={rowData?.title}
          placement="top"
          arrow
          classes={{ tooltip: classes.customTooltip }}
        >
          <Center width={rowData?.title?.length > 40 ? "250px" : "auto"}>
            {rowData?.title}
          </Center>
        </Tooltip>
      ),
      width: "31%",
    },
    {
      field: "contractNumber",
      title: "Contract number",
      render: (rowData) => <Center>{rowData?.contractNumber || "-"}</Center>,
      width: "16%",
    },
    {
      field: "document_count",
      title: "Document count",
      sorting: true,
      render: (rowData) => <Center>{rowData?.document_count}</Center>,
      width: "15%",
    },
    {
      field: "documentNames",
      title: "Document names",
      sorting: true,
      render: (rowData) => (
        <Tooltip
          title={
            <div>
              <div>{rowData?.documentNames}</div>
            </div>
          }
          placement="top"
          arrow
          classes={{ tooltip: classes.customTooltip }}
        >
          <Center
            width={rowData?.documentNames?.length > 30 ? "210px" : "auto"}
            onClick={() => {
              navigator.clipboard.writeText(rowData?.documentNames || "");
              enqueueSnackbar("Document names copied to clipboard", {
                variant: "success",
              });
            }}
            style={{ cursor: "pointer" }}
          >
            {rowData?.documentNames}
          </Center>
        </Tooltip>
      ),
      width: "22%",
    },
  ];

  const detailPanel = [
    {
      tooltip: "View document categories",
      render: (rowData) => {
        const categories = Object.entries(rowData?.categories || {});

        return (
          <DetailContainer>
            <Label>Document Categories</Label>
            {categories.length ? (
              categories.map(([category, details]) => (
                <CommentCard key={category}>
                  <CommentContent>
                    <Label>Category</Label>
                    <Value>{category}</Value>
                  </CommentContent>
                  <CommentContent>
                    <Label>Document count</Label>
                    <Value>{details?.count}</Value>
                  </CommentContent>
                  <CommentContent>
                    <Label>Document names</Label>
                    <Value>{details?.names}</Value>
                  </CommentContent>
                </CommentCard>
              ))
            ) : (
              <Value>No document categories available</Value>
            )}
          </DetailContainer>
        );
      },
    },
  ];

  const excelData = currentData?.map((item) => ({
    CaseId: item?.caseId,
    Title: item?.title,
    CaseDescription: item?.description,
    ContractNumber: item?.contractNumber,
    "Document Count": item?.document_count,
    "Document Names": item?.documentNames,
  }));

  return (
    <>
      <ActionBar
        breadcrumbs={["Dashboard", "Reports"]}
        {...{ onBreadcrumbClick, actions }}
      />

      <Container>
        <GirdContainer>
          <SearchSection>
            <Autocomplete
              multiple
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) =>
                setInputValue(newInputValue)
              }
              options={options}
              loading={loading}
              value={selectedParties}
              onChange={(event, newValue) => {
                setSelectedParties(newValue || []);
              }}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.id === value.id}
              noOptionsText={
                inputValue.length < 2
                  ? "Type at least 2 characters to search"
                  : loading
                    ? "Searching..."
                    : "No parties found"
              }
              classes={{
                listbox: classes.listbox,
                noOptions: classes.noOptions,
                paper: classes.paper,
              }}
              renderOption={(option) => (
                <OptionContainer>
                  <OptionContent>
                    <OptionName>{option.label}</OptionName>
                    <OptionEmail>{option.email}</OptionEmail>
                  </OptionContent>
                  <OptionCheck>✓</OptionCheck>
                </OptionContainer>
              )}
              renderTags={() => null} // Hide default tags — we show custom chips below
              renderInput={(params) => (
                <CustomInputField
                  {...params}
                  variant="outlined"
                  placeholder="Type to search parties..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Selected Parties as Chips */}
            <ChipContainer className={classes.root}>
              {selectedParties.map((party) => (
                <Chip
                  key={party.id}
                  variant="outlined"
                  classes={{ label: classes.chipLabel }}
                  size="medium"
                  label={`${party.label} (ID: ${party.id})`}
                  onDelete={() => {
                    setSelectedParties(
                      selectedParties.filter((p) => p.id !== party.id),
                    );
                  }}
                  deleteIcon={<Cancel className={classes.deleteIcon} />}
                />
              ))}
              {selectedParties.length === 0 && (
                <div
                  style={{
                    color: "#8294a5",
                    padding: "8px 0",
                    fontFamily: "openSans-SemiBold, sans-serif",
                  }}
                >
                  No parties selected
                </div>
              )}
            </ChipContainer>
          </SearchSection>
        </GirdContainer>

        {/* Reports Table */}
        {tableLoading ? (
          <Loader>
            <CircularProgress />
          </Loader>
        ) : (
          <CustomTable
            hidePagination={currentData?.lastPage === 1}
            pageSize={10}
            pluralTitle="Reports"
            singularTitle="Report"
            placeholderText="Search reports"
            noToolbar={true}
            columns={columns}
            excelData={excelData}
            data={currentData || []}
            state={currentData}
            detailPanel={detailPanel}
          />
        )}
      </Container>
      {state?.state && <CaseReports {...{ state, setState, options }} />}
    </>
  );
};

export default BulkUploadReports;

// Styled Components
const Container = styled.div`
  padding: 20px 43px;
  display: flex;
  flex-direction: column;
`;

const GirdContainer = styled.div`
  @media ${theme.breakpoints.sm_up} {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const SearchSection = styled.div`
  margin: 20px 0;
  max-width: 700px;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid ${COLORS.BORDER_GREY};
  min-height: 60px;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #293461;
  font-family: openSans-SemiBold, sans-serif;
  margin-bottom: 4px;
`;

const OptionEmail = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  font-family: openSans-SemiBold, sans-serif;
`;

const OptionCheck = styled.span`
  font-size: 16px;
  color: #00838c;
  opacity: 0;
  transition: opacity 0.2s ease;

  li[data-option-index]:hover & {
    opacity: 1;
  }
`;

const Center = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #293461;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: ${({ width }) => width || "auto"};
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;
